import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Bed, Plus, Trash2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

export default function BedManagement() {
  const [beds, setBeds] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [seedModal, setSeedModal] = useState(false);
  const [seedData, setSeedData] = useState({ room_id: '', num_beds: 4, gender: 'mixed', base_price: 500 });

  const loadData = useCallback(async () => {
    try {
      const [bedsRes, roomsRes] = await Promise.all([
        api.get('/beds', { params: { room_id: selectedRoom || undefined } }),
        api.get('/rooms')
      ]);
      setBeds(bedsRes.data);
      setRooms(roomsRes.data.filter(r => r.room_type === 'dormitory'));
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [selectedRoom]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSeed = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/beds/seed-dormitory/${seedData.room_id}?num_beds=${seedData.num_beds}&gender=${seedData.gender}&base_price=${seedData.base_price}`);
      toast.success('Beds created!');
      setSeedModal(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this bed?')) return;
    try { await api.delete(`/beds/${id}`); toast.success('Deleted'); loadData(); }
    catch { toast.error('Failed'); }
  };

  const dormRooms = rooms;
  const grouped = {};
  beds.forEach(b => {
    if (!grouped[b.room_id]) grouped[b.room_id] = [];
    grouped[b.room_id].push(b);
  });

  return (
    <div data-testid="bed-management-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Manrope']">Dormitory Beds</h1>
          <p className="text-stone-500 mt-1">Manage individual beds in dormitory rooms</p>
        </div>
        <Button onClick={() => setSeedModal(true)} className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="seed-beds-btn">
          <Wand2 className="w-4 h-4 mr-2" />Auto-Create Beds
        </Button>
      </div>

      {/* Filter */}
      <div className="mb-6">
        <Select value={selectedRoom || 'all'} onValueChange={v => setSelectedRoom(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-64 border-stone-300" data-testid="filter-dorm-room">
            <SelectValue placeholder="All Dormitory Rooms" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Dormitory Rooms</SelectItem>
            {dormRooms.map(r => <SelectItem key={r.id} value={r.id}>Room {r.room_number} ({r.capacity} capacity)</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div></div>
      ) : beds.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-md p-12 text-center">
          <Bed className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p className="text-stone-500 mb-4">No beds configured yet</p>
          <Button onClick={() => setSeedModal(true)} variant="outline">Create beds for a dormitory</Button>
        </div>
      ) : (
        Object.entries(grouped).map(([roomId, roomBeds]) => {
          const room = rooms.find(r => r.id === roomId);
          return (
            <div key={roomId} className="bg-white border border-stone-200 rounded-md mb-4">
              <div className="p-4 border-b border-stone-200 bg-stone-50">
                <h3 className="font-semibold text-stone-900 font-['Manrope']">
                  Room {room?.room_number || 'Unknown'} — {roomBeds[0]?.gender_restriction === 'female_only' ? 'Female Only' : 'Mixed'} Dorm
                </h3>
                <p className="text-sm text-stone-500">{roomBeds.length} beds • {roomBeds.filter(b => b.status === 'available').length} available</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 p-4">
                {roomBeds.map(bed => (
                  <div key={bed.id} className={`border rounded-md p-3 text-center relative ${
                    bed.status === 'available' ? 'border-emerald-300 bg-emerald-50' :
                    bed.status === 'occupied' ? 'border-amber-300 bg-amber-50' :
                    'border-stone-300 bg-stone-50'
                  }`} data-testid={`bed-${bed.id}`}>
                    <Bed className={`w-6 h-6 mx-auto mb-1 ${bed.status === 'available' ? 'text-emerald-700' : bed.status === 'occupied' ? 'text-amber-700' : 'text-stone-400'}`} />
                    <p className="font-medium text-sm text-stone-900">{bed.bed_label}</p>
                    <p className="text-xs text-stone-500 capitalize">{bed.bed_position.replace('_', ' ')}</p>
                    <p className="text-xs font-medium text-sage-700 mt-1">₹{bed.price_per_night}/night</p>
                    <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                      bed.status === 'available' ? 'bg-emerald-100 text-emerald-700' :
                      bed.status === 'occupied' ? 'bg-amber-100 text-amber-700' :
                      'bg-stone-100 text-stone-600'
                    }`}>{bed.status}</span>
                    <button onClick={() => handleDelete(bed.id)} className="absolute top-1 right-1 text-stone-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}

      {/* Seed Modal */}
      <Dialog open={seedModal} onOpenChange={setSeedModal}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Manrope']">Auto-Create Dormitory Beds</DialogTitle></DialogHeader>
          <form onSubmit={handleSeed} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Dormitory Room</Label>
              <Select value={seedData.room_id} onValueChange={v => setSeedData({...seedData, room_id: v})}>
                <SelectTrigger className="border-stone-300"><SelectValue placeholder="Select room" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {dormRooms.map(r => <SelectItem key={r.id} value={r.id}>Room {r.room_number}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Number of Beds</Label>
                <Input type="number" min="2" max="20" value={seedData.num_beds} onChange={e => setSeedData({...seedData, num_beds: parseInt(e.target.value)})} className="border-stone-300" />
              </div>
              <div className="space-y-2">
                <Label>Base Price/Night (₹)</Label>
                <Input type="number" min="100" value={seedData.base_price} onChange={e => setSeedData({...seedData, base_price: parseFloat(e.target.value)})} className="border-stone-300" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Gender</Label>
              <Select value={seedData.gender} onValueChange={v => setSeedData({...seedData, gender: v})}>
                <SelectTrigger className="border-stone-300"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="mixed">Mixed</SelectItem>
                  <SelectItem value="female_only">Female Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setSeedModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-sage-700 hover:bg-sage-800 text-white" disabled={!seedData.room_id}>Create Beds</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
